import Cookie from 'js-cookie';

import { Config } from '@nw/shared/config';

const ACCOUNTS_ACCESS_TOKEN_KEY = 'FktanBbpTU';
const ACCOUNTS_REFRESH_TOKEN_KEY = 'ALZzOatVxQ';
const FALL_BACK = 'BdRFSpcHFi';
const INCOGNITO_COOKIE = 'KhryuS3bST';
const USER_ID_COOKIE = 'uPruEGemmc';

interface BaseCookieOptionTypes {
  expires: number;
  path: string;
  domain?: string;
}

export function getCookie(key: string) {
  if (Cookie.get(key) !== undefined) return Cookie.get(key);
  else return Cookie.get(`${key}_${FALL_BACK}`);
}

function getDomainForCookie(
  providedDomain: string | undefined
): string | undefined {
  const isEmbedForms = process.env['APP_ENVIRONMENT'] === 'embed-forms';
  const currentDomain = window.location.hostname;

  // Note: Enable this after making the cookie domain change live in niat application
  // if (isEmbedForms && currentDomain.includes('niatindia.com')) {
  //   return process.env['NX_NIAT_APP_AUTH_COOKIE_DOMAIN'];
  // }

  return providedDomain;
}

export function setCookie(
  key: string,
  value: string,
  expiryInDays: any = 30,
  domain = undefined
) {
  const isDevelopment =
    process.env['NODE_ENV'] === 'development' ||
    window.location.hostname === 'localhost';

  const baseCookieOptions: BaseCookieOptionTypes = {
    expires: expiryInDays,
    path: '/',
    ...(isDevelopment ? {} : { secure: true, sameSite: 'none' })
  };

  if (isDevelopment) {
    Cookie.set(key, value, baseCookieOptions);
    return;
  }

  const cookieDomain = getDomainForCookie(domain);

  if (cookieDomain) {
    Cookie.set(key, value, {
      ...baseCookieOptions,
      domain: cookieDomain
    });
    Cookie.set(`${key}_${FALL_BACK}`, value, {
      ...baseCookieOptions,
      domain: cookieDomain,
      secure: true,
      sameSite: 'none'
    });
  } else {
    Cookie.set(key, value, baseCookieOptions);
    Cookie.set(`${key}_${FALL_BACK}`, value, {
      ...baseCookieOptions,
      secure: true,
      sameSite: 'none'
    });
  }
}

export function getAccessToken() {
  return getCookie(ACCOUNTS_ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return getCookie(ACCOUNTS_REFRESH_TOKEN_KEY);
}

export const getCookieAttrubutes = () => {
  let neededAttributes: Record<string, any> = {
    path: '/'
  };
  if (
    process.env['NODE_ENV'] !== 'development' &&
    window.location.hostname !== 'localhost'
  ) {
    neededAttributes = { ...neededAttributes, secure: true, sameSite: 'None' };
  }
  return neededAttributes;
};

export const clearAccessToken = (domain?: string) => {
  Cookie.remove(ACCOUNTS_ACCESS_TOKEN_KEY, {
    ...getCookieAttrubutes(),
    domain: domain
  });
};

export const clearRefreshToken = (domain?: string) => {
  Cookie.remove(ACCOUNTS_REFRESH_TOKEN_KEY, {
    ...getCookieAttrubutes(),
    domain: domain
  });
};

export const clearAuthDomainTokens = () => {
  const appCookieDomain =
    Config['APP_COOKIE_DOMAIN'] || Config['APP_AUTH_COOKIE_DOMAIN'];
  if (appCookieDomain) {
    clearAccessToken(appCookieDomain);
    clearRefreshToken(appCookieDomain);
  }
};

export function setAccessTokenToDomain(accessToken: string, expiryInDays = 30) {
  setCookie(
    ACCOUNTS_ACCESS_TOKEN_KEY,
    accessToken,
    expiryInDays,
    Config['APP_AUTH_COOKIE_DOMAIN']
  );
}

export function setRefreshTokenToDomain(
  refereshToken: string,
  expiryInDays = 30
) {
  setCookie(
    ACCOUNTS_REFRESH_TOKEN_KEY,
    refereshToken,
    expiryInDays,
    Config['APP_AUTH_COOKIE_DOMAIN']
  );
}

export function clearUserSession() {
  const isDevelopment =
    process.env['NODE_ENV'] === 'development' ||
    window.location.hostname === 'localhost';

  const baseAttributes = {
    path: '/',
    ...(isDevelopment ? {} : { secure: true, sameSite: 'none' })
  };

  const cookies = Object.keys(Cookie.get());

  cookies.forEach((cookieName) => {
    if (
      cookieName !== INCOGNITO_COOKIE &&
      cookieName !== `${INCOGNITO_COOKIE}_${FALL_BACK}`
    ) {
      Cookie.remove(cookieName, baseAttributes);

      if (Config['APP_AUTH_COOKIE_DOMAIN']) {
        Cookie.remove(cookieName, {
          ...baseAttributes,
          domain: Config['APP_AUTH_COOKIE_DOMAIN']
        });
      }

      Cookie.remove(`${cookieName}_${FALL_BACK}`, baseAttributes);
      if (Config['APP_AUTH_COOKIE_DOMAIN']) {
        Cookie.remove(`${cookieName}_${FALL_BACK}`, {
          ...baseAttributes,
          domain: Config['APP_AUTH_COOKIE_DOMAIN']
        });
      }
    }
  });
}

export function getUserId() {
  return getCookie(USER_ID_COOKIE);
}

export function setUserId(userId: string, expiryInDays = 30) {
  setCookie(
    USER_ID_COOKIE,
    userId,
    expiryInDays,
    Config['APP_AUTH_COOKIE_DOMAIN']
  );
}
